import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { WhatsAppChatPanel } from "@/components/admin/WhatsAppChatPanel";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useEmpresa } from "@/contexts/EmpresaContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { CheckCircle2, Clock3, MessageSquareText, PlugZap, RefreshCcw } from "lucide-react";

type ConnectionStatus = "desconectado" | "aguardando_leitura" | "conectado";

type PersistedState = "close" | "connecting" | "open" | "connected";

const STORED_STATE_TO_CONNECTION_STATUS: Record<PersistedState, ConnectionStatus> = {
  close: "desconectado",
  connecting: "aguardando_leitura",
  open: "conectado",
  connected: "conectado",
};

const STATUS_CONFIG: Record<
  ConnectionStatus,
  { label: string; className: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  desconectado: {
    label: "Desconectado",
    className: "bg-slate-100 text-slate-700",
    Icon: PlugZap,
  },
  aguardando_leitura: {
    label: "Aguardando leitura do QR",
    className: "bg-amber-100 text-amber-800",
    Icon: Clock3,
  },
  conectado: {
    label: "Conectado",
    className: "bg-emerald-100 text-emerald-800",
    Icon: CheckCircle2,
  },
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const toConnectionStatus = (value: unknown): ConnectionStatus => {
  const state = typeof value === "string" ? value : "close";
  if (state === "close" || state === "connecting" || state === "open" || state === "connected") {
    return STORED_STATE_TO_CONNECTION_STATUS[state];
  }

  return "desconectado";
};

const formatDateTime = (isoDate: string | null) => {
  if (!isoDate) return "-";
  return new Date(isoDate).toLocaleString("pt-BR");
};

export default function AdminWhatsAppChat() {
  const { empresaId, isLoading: isEmpresaLoading } = useEmpresa();

  const [isLoadingInstance, setIsLoadingInstance] = useState(false);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
  const [instanceName, setInstanceName] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [requestedNumber, setRequestedNumber] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("desconectado");

  const statusConfig = useMemo(() => STATUS_CONFIG[connectionStatus], [connectionStatus]);
  const isConnected = connectionStatus === "conectado";

  const loadPersistedInstance = useCallback(async () => {
    if (!empresaId) return;

    setIsLoadingInstance(true);

    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-connection-state", {
        body: {
          empresa_id: empresaId,
          persistedOnly: true,
        },
      });

      if (error) {
        throw new Error(error.message || "Falha ao consultar instância WhatsApp");
      }

      if (!isRecord(data) || !isRecord(data.instance)) {
        setInstanceName(null);
        setSessionId(null);
        setRequestedNumber(null);
        setLastUpdatedAt(null);
        setConnectionStatus("desconectado");
        return;
      }

      const persistedInstance = data.instance;

      setInstanceName(typeof persistedInstance.instanceName === "string" ? persistedInstance.instanceName : null);
      setSessionId(
        typeof persistedInstance.id === "number" || typeof persistedInstance.id === "string"
          ? String(persistedInstance.id)
          : null,
      );
      setRequestedNumber(
        typeof persistedInstance.phone_number === "string" && persistedInstance.phone_number.trim()
          ? persistedInstance.phone_number.trim()
          : null,
      );
      setLastUpdatedAt(
        typeof persistedInstance.updated_at === "string"
          ? persistedInstance.updated_at
          : typeof persistedInstance.created_at === "string"
            ? persistedInstance.created_at
            : null,
      );
      setConnectionStatus(toConnectionStatus(persistedInstance.state));
    } catch (error) {
      console.error("Erro ao carregar instância persistida:", error);
      const message = error instanceof Error ? error.message : "Falha ao carregar instância";
      toast.error("Erro ao carregar instância", { description: message });
    } finally {
      setIsLoadingInstance(false);
    }
  }, [empresaId]);

  const refreshConnectionStatus = useCallback(async () => {
    if (!empresaId || !instanceName) {
      await loadPersistedInstance();
      return;
    }

    setIsRefreshingStatus(true);

    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-connection-state", {
        body: {
          empresa_id: empresaId,
          instanceName,
        },
      });

      if (error) {
        throw new Error(error.message || "Falha ao atualizar status da conexão");
      }

      if (isRecord(data) && isRecord(data.instance)) {
        const payloadInstance = data.instance;

        setConnectionStatus(toConnectionStatus(payloadInstance.state));
        setLastUpdatedAt(
          typeof payloadInstance.updated_at === "string" ? payloadInstance.updated_at : new Date().toISOString(),
        );

        if (typeof payloadInstance.instanceName === "string" && payloadInstance.instanceName.trim()) {
          setInstanceName(payloadInstance.instanceName.trim());
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar status da instância:", error);
      const message = error instanceof Error ? error.message : "Falha ao atualizar status";
      toast.error("Erro ao atualizar status", { description: message });
    } finally {
      setIsRefreshingStatus(false);
    }
  }, [empresaId, instanceName, loadPersistedInstance]);

  useEffect(() => {
    if (isEmpresaLoading || !empresaId) return;
    void loadPersistedInstance();
  }, [empresaId, isEmpresaLoading, loadPersistedInstance]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Ambiente de Chat WhatsApp"
          subtitle="Operação de conversas em tempo real com integração completa da Evolution API."
          backTo="/admin/control-center"
          rightTop={
            <Button variant="outline" asChild>
              <Link to="/admin/communication">Ir para conexão WhatsApp</Link>
            </Button>
          }
        />

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquareText className="h-5 w-5 text-slate-700" />
              Status da instância para chat
            </CardTitle>
            <CardDescription>
              Este ambiente usa a instância já conectada na Central de Comunicação.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="flex items-center gap-2">
                <statusConfig.Icon className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">Status atual</span>
              </div>

              <div className="flex items-center gap-2">
                <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refreshConnectionStatus()}
                  disabled={isEmpresaLoading || isLoadingInstance || isRefreshingStatus}
                  className="gap-2"
                >
                  <RefreshCcw className={`h-4 w-4 ${isRefreshingStatus ? "animate-spin" : ""}`} />
                  Atualizar status
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 text-xs text-slate-500 sm:grid-cols-2 lg:grid-cols-4">
              <p>
                <span className="font-medium text-slate-700">Instância:</span> {instanceName || "-"}
              </p>
              <p>
                <span className="font-medium text-slate-700">ID da sessão:</span> {sessionId || "-"}
              </p>
              <p>
                <span className="font-medium text-slate-700">Número:</span> {requestedNumber ? `+${requestedNumber}` : "-"}
              </p>
              <p>
                <span className="font-medium text-slate-700">Última atualização:</span> {formatDateTime(lastUpdatedAt)}
              </p>
            </div>
          </CardContent>
        </Card>

        <WhatsAppChatPanel
          empresaId={empresaId}
          isEmpresaLoading={isEmpresaLoading || isLoadingInstance}
          instanceName={instanceName}
          isConnected={isConnected}
        />
      </div>
    </AdminLayout>
  );
}
